/**
 * Canonical Guardians cross-navigation layer.
 *
 * Every "View X" surface (audit inspector, scope chips, alert panel, timeline)
 * builds a normalized {@link EntityRef} and runs it through {@link entityDeepLink}
 * to get a single source of truth for the label, the shareable href, the focus
 * key (for the post-navigation highlight), and a `run(deps)` navigation action.
 *
 * This replaces the previously divergent builders (`getAuditSubjectPath`,
 * `auditSubjectNavigation`, `timelineEntityNavigation`).
 */

import type { Audit, Alert, AuditScope, AuditSubject } from "@/services/api/guardiansv2/types";
import type { MissionTimelineItem } from "@/services/api/agentic-v2/types";

export type EntityKind =
  | "trigger"
  | "mission"
  | "task"
  | "interaction"
  | "tool_call"
  | "artifact";

/** Normalized, app-agnostic reference to a Guardians-auditable entity. */
export interface EntityRef {
  kind: EntityKind;
  /** The entity's own id. For tool calls this is the parent interaction id. */
  id: string;
  /** Tool-call step index (subject version). */
  version?: number | null;
  scope: {
    mission_id?: string | null;
    task_id?: string | null;
    interaction_id?: string | null;
  };
  /** Optional display title (used for window titles where relevant). */
  title?: string | null;
}

export interface EntityDeepLinkDeps {
  osNavigate: (path: string) => void;
}

export interface EntityDeepLink {
  kind: EntityKind;
  label: string;
  /** Absolute-from-origin path with the focus param baked in. */
  href: string;
  /** `<subject_type>:<id>[:version]` — matches timeline `subjectKey` + audit subject. */
  focusKey: string;
  run: (deps: EntityDeepLinkDeps) => void;
}

const KIND_LABEL: Record<EntityKind, string> = {
  trigger: "trigger",
  mission: "mission",
  task: "task",
  interaction: "interaction",
  tool_call: "tool call",
  artifact: "artifact",
};

/** Subject-type token used in focus keys / timeline subject keys. */
const KIND_SUBJECT_TYPE: Record<EntityKind, string> = {
  trigger: "trigger_event",
  mission: "mission",
  task: "task",
  interaction: "interaction",
  tool_call: "step",
  artifact: "artifact",
};

export function entityKindLabel(kind: EntityKind): string {
  return KIND_LABEL[kind] ?? "entity";
}

function focusKeyFor(ref: EntityRef): string {
  const base = `${KIND_SUBJECT_TYPE[ref.kind]}:${ref.id}`;
  return ref.version != null ? `${base}:${ref.version}` : base;
}

function withParams(path: string, params: Record<string, string | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== "") search.set(key, value);
  }
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
}

/** Build the canonical href for a ref, or null when there is no sensible target. */
function hrefFor(ref: EntityRef, focusKey: string): string | null {
  const enc = encodeURIComponent;
  switch (ref.kind) {
    case "trigger":
      return withParams("/ops/triggers", {
        tab: "plannr-triggers",
        event: ref.id,
        focus: focusKey,
      });
    case "mission": {
      const id = ref.scope.mission_id || ref.id;
      if (!id) return null;
      return withParams(`/missions/${enc(id)}`, { focus: focusKey });
    }
    case "task":
      return withParams(`/mission-control/tasks/${enc(ref.id)}`, { focus: focusKey });
    case "interaction":
    case "tool_call": {
      const taskId = ref.scope.task_id;
      if (taskId) {
        return withParams(`/mission-control/tasks/${enc(taskId)}`, { focus: focusKey });
      }
      const missionId = ref.scope.mission_id;
      if (missionId) {
        return withParams(`/missions/${enc(missionId)}`, { focus: focusKey });
      }
      return null;
    }
    case "artifact":
      return `/artifacts/${enc(ref.id)}`;
    default:
      return null;
  }
}

/** Resolve a ref into label + href + focus key + run action, or null if unroutable. */
export function entityDeepLink(ref: EntityRef): EntityDeepLink | null {
  const focusKey = focusKeyFor(ref);
  const href = hrefFor(ref, focusKey);
  if (!href) return null;
  return {
    kind: ref.kind,
    label: `View ${entityKindLabel(ref.kind)}`,
    href,
    focusKey,
    run: (deps) => deps.osNavigate(href),
  };
}

const SUBJECT_TYPE_TO_KIND: Record<string, EntityKind> = {
  trigger_event: "trigger",
  mission: "mission",
  task: "task",
  interaction: "interaction",
  step: "tool_call",
  artifact: "artifact",
};

/** Build an {@link EntityRef} from an audit/alert subject + scope. */
export function entityRefFromSubject(
  subject: AuditSubject,
  scope: AuditScope,
  title?: string | null
): EntityRef | null {
  const kind = SUBJECT_TYPE_TO_KIND[subject.type];
  if (!kind) return null;
  const id = kind === "mission" ? scope.mission_id || subject.id : subject.id;
  if (!id) return null;
  return {
    kind,
    id,
    version: subject.version ?? null,
    scope: {
      mission_id: scope.mission_id ?? null,
      task_id: scope.task_id ?? null,
      interaction_id: scope.interaction_id ?? null,
    },
    title: title ?? null,
  };
}

/** Build an {@link EntityRef} from an audit (driven by `subject`, scope-aware). */
export function entityRefFromAudit(audit: Audit): EntityRef | null {
  return entityRefFromSubject(audit.subject, audit.scope, audit.inputs?.summary ?? null);
}

/** Build an {@link EntityRef} from an alert. */
export function entityRefFromAlert(alert: Alert): EntityRef | null {
  return entityRefFromSubject(alert.subject, alert.scope, alert.summary ?? null);
}

const TIMELINE_KIND_TO_ENTITY: Record<MissionTimelineItem["kind"], EntityKind> = {
  mission: "mission",
  task: "task",
  interaction: "interaction",
  tool_call: "tool_call",
  artifact: "artifact",
  trigger: "trigger",
};

/** Build an {@link EntityRef} from a mission-timeline row. */
export function entityRefFromTimelineItem(
  item: MissionTimelineItem,
  missionId: string
): EntityRef | null {
  const kind = TIMELINE_KIND_TO_ENTITY[item.kind];
  if (!kind) return null;
  const id =
    kind === "mission"
      ? item.scope.mission_id || item.subject_id
      : kind === "task"
        ? item.scope.task_id || item.subject_id
        : item.subject_id;
  if (!id) return null;
  return {
    kind,
    id,
    version: item.subject_version ?? null,
    scope: {
      mission_id: item.scope.mission_id ?? missionId ?? null,
      task_id: item.scope.task_id ?? null,
      interaction_id: item.scope.interaction_id ?? null,
    },
    title: item.title ?? null,
  };
}
